/**
 * Communication Detail Page — View and interact with a communication
 * 
 * Features:
 * - Full communication view
 * - AI suggestions with execute buttons
 * - Voice transcript review/edit
 * - Reply functionality
 * - Thread view
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, MessageSquare, Mic, Clock, User, FileText, 
  Sparkles, CheckCircle2, XCircle, Edit2, Save, Play, Pause,
  Send, AlertTriangle, RefreshCw, ExternalLink, Phone, Pin,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';

// Suggestion type config
const SUGGESTION_CONFIG = {
  create_incident: { 
    icon: AlertTriangle, 
    color: 'bg-red-100 text-red-700 border-red-200',
    label: 'Create Incident Report'
  },
  notify_supervisor: { 
    icon: User, 
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    label: 'Notify Supervisor'
  },
  notify_family: { 
    icon: Phone, 
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    label: 'Notify Family'
  },
  update_care_plan: { 
    icon: FileText, 
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    label: 'Update Care Plan'
  },
  create_task: { 
    icon: CheckCircle2, 
    color: 'bg-green-100 text-green-700 border-green-200',
    label: 'Create Task'
  },
  document_only: { 
    icon: FileText, 
    color: 'bg-gray-100 text-gray-700 border-gray-200',
    label: 'Document Only'
  },
  notify_all: { 
    icon: MessageSquare, 
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    label: 'Notify Care Team'
  },
};

export default function CommunicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [communication, setCommunication] = useState(null);
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Voice transcript editing
  const [editingTranscript, setEditingTranscript] = useState(false);
  const [transcriptEdit, setTranscriptEdit] = useState('');
  const [savingTranscript, setSavingTranscript] = useState(false);
  
  // Reply
  const [showReply, setShowReply] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  
  // Suggestions
  const [executingSuggestion, setExecutingSuggestion] = useState(null);

  // Fetch communication
  const fetchCommunication = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const res = await api.get(`/communications/${id}`);
      setCommunication(res.data);
      
      // Mark as read
      await api.post(`/communications/${id}/read`).catch(() => {});
      
      // Fetch thread if exists
      if (res.data.thread_id) {
        const threadRes = await api.get(`/communications/threads/${res.data.thread_id}`);
        setThread(threadRes.data);
      }
      
    } catch (err) {
      console.error('Failed to fetch communication:', err);
      setError('Failed to load communication');
    } finally {
      setLoading(false);
    }
  }, [id]);
  
  useEffect(() => {
    fetchCommunication();
  }, [fetchCommunication]);
  
  // Handle transcript edit
  const handleSaveTranscript = async (finalize = false) => {
    try {
      setSavingTranscript(true);
      
      await api.put(`/communications/${id}/voice/transcript`, {
        transcript_edited: transcriptEdit,
        finalize,
      });
      
      await fetchCommunication();
      setEditingTranscript(false);
      
    } catch (err) {
      console.error('Failed to save transcript:', err);
    } finally {
      setSavingTranscript(false);
    }
  };
  
  // Handle AI suggestion action
  const handleSuggestionAction = async (suggestionId, decision, execute = false) => {
    try {
      setExecutingSuggestion(suggestionId);
      
      await api.post(`/communications/${id}/suggestions/${suggestionId}`, {
        suggestion_id: suggestionId,
        decision,
        execute,
      });
      
      await fetchCommunication();
      
    } catch (err) {
      console.error('Failed to act on suggestion:', err);
    } finally {
      setExecutingSuggestion(null);
    }
  };
  
  // Handle reply
  const handleReply = async () => {
    if (!replyContent.trim()) return;
    
    try {
      setSendingReply(true);
      
      await api.post('/communications', {
        type: 'message',
        content: replyContent.trim(),
        parent_id: id,
        thread_id: communication?.thread_id,
        primary_context: communication?.contexts?.[0] ? {
          entity_type: communication.contexts[0].entity_type,
          entity_id: communication.contexts[0].entity_id,
        } : null,
      });
      
      setReplyContent('');
      setShowReply(false);
      await fetchCommunication();
      
    } catch (err) {
      console.error('Failed to send reply:', err);
    } finally {
      setSendingReply(false);
    }
  };
  
  // Handle acknowledge
  const handleAcknowledge = async () => {
    try {
      await api.post(`/communications/${id}/acknowledge`);
      await fetchCommunication();
    } catch (err) {
      console.error('Failed to acknowledge:', err);
    }
  };
  
  // Format time
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }
  
  if (error || !communication) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <AlertTriangle className="w-12 h-12 text-red-400 mb-3" />
        <p className="text-gray-600">{error || 'Communication not found'}</p>
        <Button variant="outline" onClick={() => navigate('/communications')} className="mt-4">
          Back to Communications
        </Button>
      </div>
    );
  }

  const isVoiceMessage = communication.type === 'voice_message' && communication.voice_message;
  const hasAiSuggestions = communication.ai_suggestions?.length > 0;
  const needsAcknowledgment = communication.requires_acknowledgment && 
    !communication.acknowledged_by?.some(a => a.user_id === user?.id);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="communication-detail">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate('/communications')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            
            <div className="flex items-center gap-2">
              {communication.is_pinned && (
                <Badge variant="outline">
                  <Pin className="w-3 h-3 mr-1" />
                  Pinned
                </Badge>
              )}
              <Badge className={`capitalize ${
                communication.priority === 'urgent' || communication.priority === 'emergency'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {communication.priority}
              </Badge>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Communication Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-start justify-between mb-4">
              <div>
                {communication.subject && (
                  <h1 className="text-xl font-semibold text-gray-900 mb-1">
                    {communication.subject}
                  </h1>
                )}
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <User className="w-4 h-4" />
                  <span className="font-medium text-gray-900">{communication.sender_name}</span>
                  {communication.sender_role && (
                    <span className="text-gray-400">• {communication.sender_role}</span>
                  )}
                </div>
              </div>
              
              <div className="text-sm text-gray-500 text-right">
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{formatTime(communication.created_at)}</span>
                </div>
              </div>
            </div>
            
            {/* Contexts */}
            {communication.contexts?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {communication.contexts.map((ctx, i) => (
                  <button
                    key={i}
                    onClick={() => navigate(`/communications?entity_type=${ctx.entity_type}&entity_id=${ctx.entity_id}`)}
                    className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100 transition-colors"
                  >
                    <FileText className="w-3 h-3" />
                    <span className="capitalize">{ctx.entity_type}</span>
                    {ctx.entity_name && (
                      <span className="font-medium">: {ctx.entity_name}</span>
                    )}
                    <ExternalLink className="w-3 h-3" />
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Content */}
          <div className="p-6">
            {/* Voice Message Section */}
            {isVoiceMessage && (
              <VoiceMessageSection
                voiceMessage={communication.voice_message}
                editingTranscript={editingTranscript}
                setEditingTranscript={setEditingTranscript}
                transcriptEdit={transcriptEdit}
                setTranscriptEdit={setTranscriptEdit}
                savingTranscript={savingTranscript}
                onSave={handleSaveTranscript}
              />
            )}
            
            {/* Text Content */}
            <div className="prose prose-sm max-w-none">
              <p className="text-gray-700 whitespace-pre-wrap">{communication.content}</p>
            </div>
            
            {/* Attachments */}
            {communication.attachments?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Attachments</h3>
                <div className="flex flex-wrap gap-2">
                  {communication.attachments.map((att, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg text-sm"
                    >
                      <FileText className="w-4 h-4 text-gray-400" />
                      <span>{att.file_name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* AI Suggestions */}
          {hasAiSuggestions && (
            <AiSuggestionsSection
              suggestions={communication.ai_suggestions}
              executingSuggestion={executingSuggestion}
              onAction={handleSuggestionAction}
            />
          )}
          
          {/* Acknowledgment */}
          {needsAcknowledgment && (
            <div className="p-4 bg-amber-50 border-t border-amber-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">This communication requires acknowledgment</span>
                </div>
                <Button
                  onClick={handleAcknowledge}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Acknowledge
                </Button>
              </div>
            </div>
          )}
          
          {/* Reply Section */}
          <div className="p-4 bg-gray-50 border-t border-gray-200">
            {showReply ? (
              <div className="space-y-3">
                <Textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="Type your reply..."
                  rows={3}
                  autoFocus
                  data-testid="reply-input"
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowReply(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleReply}
                    disabled={!replyContent.trim() || sendingReply}
                    className="bg-[var(--color-primary)] hover:bg-[var(--color-primary)]/90"
                    data-testid="send-reply-btn"
                  >
                    {sendingReply ? (
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Reply
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => setShowReply(true)}
                className="w-full"
                data-testid="show-reply-btn"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Reply to this communication
              </Button>
            )}
          </div>
        </div>
        
        {/* Thread Messages */}
        {thread && thread.communications?.length > 1 && (
          <ThreadSection
            thread={thread}
            currentId={id}
            onSelect={(commId) => navigate(`/communications/${commId}`)}
          />
        )}
      </div>
    </div>
  );
}

// Voice Message Section Component
function VoiceMessageSection({ 
  voiceMessage, 
  editingTranscript, 
  setEditingTranscript,
  transcriptEdit,
  setTranscriptEdit,
  savingTranscript,
  onSave 
}) {
  const [showOriginal, setShowOriginal] = useState(false);
  
  const hasEdited = voiceMessage.transcript_edited && 
    voiceMessage.transcript_edited !== voiceMessage.transcript_raw;
  
  return (
    <div className="mb-4 p-4 bg-teal-50 rounded-lg border border-teal-100" data-testid="voice-section">
      {/* Audio indicator */}
      <div className="flex items-center gap-2 mb-3">
        <div className="p-2 bg-teal-100 rounded-full">
          <Mic className="w-4 h-4 text-teal-700" />
        </div>
        <div>
          <span className="text-sm font-medium text-teal-800">Voice Message</span>
          {voiceMessage.duration_seconds > 0 && (
            <span className="text-sm text-teal-600 ml-2">
              ({Math.floor(voiceMessage.duration_seconds / 60)}:{String(Math.floor(voiceMessage.duration_seconds % 60)).padStart(2, '0')})
            </span>
          )}
        </div>
      </div>
      
      {/* Transcript */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Transcript</span>
          <div className="flex items-center gap-2">
            {hasEdited && (
              <button
                onClick={() => setShowOriginal(!showOriginal)}
                className="text-xs text-teal-600 hover:text-teal-700"
              >
                {showOriginal ? 'Show Final' : 'Show Original'}
              </button>
            )}
            {!editingTranscript && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setTranscriptEdit(voiceMessage.transcript_final || voiceMessage.transcript_raw || '');
                  setEditingTranscript(true);
                }}
              >
                <Edit2 className="w-3 h-3 mr-1" />
                Edit
              </Button>
            )}
          </div>
        </div>
        
        {editingTranscript ? (
          <div className="space-y-2">
            <Textarea
              value={transcriptEdit}
              onChange={(e) => setTranscriptEdit(e.target.value)}
              rows={4}
              className="w-full"
              data-testid="transcript-edit"
            />
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditingTranscript(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onSave(false)}
                disabled={savingTranscript}
              >
                Save Draft
              </Button>
              <Button
                size="sm"
                onClick={() => onSave(true)}
                disabled={savingTranscript}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                {savingTranscript ? (
                  <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                ) : (
                  <Save className="w-3 h-3 mr-1" />
                )}
                Finalize
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-3 bg-white rounded border border-teal-200 text-sm text-gray-700">
            {showOriginal 
              ? voiceMessage.transcript_raw 
              : (voiceMessage.transcript_final || voiceMessage.transcript_raw || 'No transcript available')}
          </div>
        )}
      </div>
    </div>
  );
}

// AI Suggestions Section Component
function AiSuggestionsSection({ suggestions, executingSuggestion, onAction }) {
  const [expanded, setExpanded] = useState(true);
  
  const pendingSuggestions = suggestions.filter(s => s.status === 'pending');
  const actedSuggestions = suggestions.filter(s => s.status !== 'pending');
  
  return (
    <div className="border-t border-gray-200" data-testid="ai-suggestions">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-500" />
          <span className="font-medium text-gray-900">AI Suggestions</span>
          <Badge className="bg-amber-100 text-amber-700">
            {pendingSuggestions.length} pending
          </Badge>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      
      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          <p className="text-sm text-gray-500">
            AI suggests these follow-up actions based on the communication content.
            <strong className="text-gray-700"> You decide what to do.</strong>
          </p>
          
          {pendingSuggestions.map((suggestion) => {
            const config = SUGGESTION_CONFIG[suggestion.suggestion_type] || SUGGESTION_CONFIG.document_only;
            const Icon = config.icon;
            const isExecuting = executingSuggestion === suggestion.id;
            
            return (
              <div
                key={suggestion.id}
                className={`p-4 rounded-lg border ${config.color}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <Icon className="w-5 h-5 mt-0.5" />
                    <div>
                      <h4 className="font-medium">{suggestion.title}</h4>
                      <p className="text-sm opacity-80 mt-1">{suggestion.description}</p>
                      {suggestion.confidence > 0 && (
                        <p className="text-xs mt-1 opacity-70">
                          Confidence: {Math.round(suggestion.confidence * 100)}%
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onAction(suggestion.id, 'declined')}
                      disabled={isExecuting}
                      className="border-current"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Dismiss
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => onAction(suggestion.id, 'accepted', true)}
                      disabled={isExecuting}
                      className="bg-current text-white hover:opacity-90"
                    >
                      {isExecuting ? (
                        <RefreshCw className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                      )}
                      Execute
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
          
          {actedSuggestions.length > 0 && (
            <div className="pt-2 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-2">Previously acted on:</p>
              {actedSuggestions.map((s) => (
                <div key={s.id} className="text-sm text-gray-600 flex items-center gap-2">
                  {s.status === 'accepted' ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-gray-400" />
                  )}
                  <span>{s.title}</span>
                  <span className="text-xs text-gray-400">({s.status})</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Thread Section Component
function ThreadSection({ thread, currentId, onSelect }) {
  return (
    <div className="mt-6" data-testid="thread-section">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="w-5 h-5 text-gray-400" />
        <h2 className="text-lg font-medium text-gray-900">
          Thread: {thread.thread?.subject || 'Conversation'}
        </h2>
        <Badge variant="outline">{thread.communications?.length} messages</Badge>
      </div>
      
      <div className="space-y-2">
        {thread.communications?.map((comm) => (
          <div
            key={comm.id}
            onClick={() => comm.id !== currentId && onSelect(comm.id)}
            className={`p-3 rounded-lg border transition-colors ${
              comm.id === currentId
                ? 'bg-[var(--color-primary)]/5 border-[var(--color-primary)]/30'
                : 'bg-white border-gray-200 hover:border-gray-300 cursor-pointer'
            }`}
          >
            <div className="flex items-center gap-2 text-sm mb-1">
              <User className="w-4 h-4 text-gray-400" />
              <span className="font-medium">{comm.sender_name}</span>
              <span className="text-gray-400">•</span>
              <span className="text-gray-500">
                {new Date(comm.created_at).toLocaleString()}
              </span>
              {comm.id === currentId && (
                <Badge className="bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
                  Current
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-600 line-clamp-2">{comm.content}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
